import { type ReactNode } from "react";
import FocusTrap from "focus-trap-react";
import { MinusIcon, XIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useDockedFootprint } from "../utils/useDockedFootprint";
import { useOnboardingStepNavigation } from "../state/useOnboardingStepNavigation";
import { ONBOARDING_STEP_HEADING_ID } from "./OnboardingStepHeader";
import { StepContentMotion } from "./OnboardingSurface";
import { useOnboardingSurface } from "../state/useOnboardingSurface";

interface OnboardingModalProps {
  /**
   * Sticky block under the header bar — the step heading, and on the city step
   * the search field with it. It does not scroll: the city rows move under the
   * search box rather than taking it with them.
   */
  stickyTop?: ReactNode;
  /** The scrolling body between the sticky top and the footer. */
  children?: ReactNode;
  /** Sticky footer, normally the primary action. Omitted on the city step. */
  footer?: ReactNode;
  /** The modal header bar's ✕. The docked sheet has no ✕, only a minimise. */
  onClose: () => void;
  /**
   * What assistive technology calls the dialog. The visible heading lives in
   * `stickyTop` and changes per step, so the name is passed rather than
   * pointed at.
   */
  ariaLabel: string;
}

/**
 * The onboarding sheet, in whichever presentation the flow is in — @see
 * `onboardingV2PresentationAtom`. Every step renders through this, so no step
 * knows or cares which one it is in.
 *
 * - **Modal**: a 375px card centred over a dimmed page (`15984:127545`),
 *   after a sign-in or sign-up. Described below.
 * - **Docked**: `DockedSheet`, the less intrusive version (`16150:269296`).
 * - **Sign-up**: `SignUpCard`, inside the auth modal (`16150:279245`).
 *
 * The modal:
 *
 * The frame is literally the native screen dropped onto desktop, down to the
 * 375px width, so the chrome mirrors native's `OnboardingLayout` - sticky top,
 * scrolling body, sticky footer - inside a modal rather than a screen.
 *
 * **A fixed 691px tall, not shrink-wrapped.** Figma draws the sheet at 375x691.2
 * with its slot at `h-full` and the sheet `flex-1` inside it, so every step is
 * the same size and the footer sits at the bottom whether the body fills it or
 * not - which is what the step-1 frame shows, a clear gap above the button.
 * Sizing to content instead made short steps squat and the modal jump height
 * between steps. `max-h-full` inside the overlay's own 16px padding is what
 * shrinks it on a viewport too short for 691, so it never touches the edges.
 *
 * The 34px bottom padding the frame carries is the iOS home-indicator inset
 * from the RN screen it was traced from; on the web it is just an uneven
 * gutter, so it is 16 here and matches the sides.
 *
 * Deliberately **not** `react-responsive-modal` like the carousel it replaces:
 * that component closes on Escape and on an overlay click with no way to opt
 * out per-instance, and a first-run flow should not vanish because someone
 * clicked past it. ✕ is the only way out, and it is explicit about what it
 * means — @see `useOnboardingFlow().dismiss`.
 */
const OnboardingModal = (props: OnboardingModalProps) => {
  // From the enclosing layer, not the flow's atoms - @see `OnboardingSurface`.
  const { presentation } = useOnboardingSurface();
  if (presentation === "signup") return <SignUpCard {...props} />;
  return presentation === "docked" ? (
    <DockedSheet {...props} />
  ) : (
    <CentredModal {...props} />
  );
};

/**
 * The card inside the auth modal, straight after an account is created
 * (`16150:279245`).
 *
 * The modal's card, minus what the auth modal already provides: no overlay, no
 * focus trap, no scroll lock of its own. And no ✕ — the frame's header bar
 * hides both its slots and draws a segmented progress bar instead, one segment
 * per step, filled up to the step on screen.
 *
 * The bar is decorative; "Step N of M" under it says the same thing in words.
 */
const SignUpCard = ({
  stickyTop,
  children,
  footer,
  ariaLabel,
}: OnboardingModalProps) => {
  const { stepNumber, totalSteps } = useOnboardingStepNavigation();

  return (
    <section
      aria-label={ariaLabel}
      className="mx-auto flex h-[691px] max-h-[calc(100dvh-48px)] w-[375px] max-w-full flex-col overflow-hidden rounded-lg border border-border-token-brand-subtle bg-container-neutral-white shadow-[0px_2px_6px_0px_rgba(7,7,8,0.12)]"
    >
      {/* Header bar (`1103:53112`): four 48x8 segments, 10 apart. */}
      <div
        aria-hidden="true"
        className="flex h-14 shrink-0 items-center justify-center gap-2.5 px-1"
      >
        {Array.from({ length: totalSteps }, (_, index) => (
          <span
            key={index}
            className={cn(
              "h-2 w-12 rounded-full transition-colors duration-300",
              index < (stepNumber ?? 0)
                ? "bg-bg-brand-primary-default"
                : "bg-fg-neutral-minimal",
            )}
          />
        ))}
      </div>

      <div className="h-px w-full shrink-0 bg-border-token-brand-minimal" />

      <SheetContent stickyTop={stickyTop} footer={footer}>
        {children}
      </SheetContent>
    </section>
  );
};

/**
 * The sheet's scrolling body and sticky footer, shared by both presentations —
 * they differ only in the chrome around them.
 *
 * The heading and body slide in on a step change; the footer stays put, since
 * its button is the same from one step to the next.
 */
const SheetContent = ({
  stickyTop,
  children,
  footer,
}: Pick<OnboardingModalProps, "stickyTop" | "children" | "footer">) => (
  <>
    <StepContentMotion className="flex min-h-0 flex-1 flex-col">
      {stickyTop ? <div className="shrink-0 px-4 pt-6">{stickyTop}</div> : null}

      {/* Body (`974:3399`): 16pt gutters, 24 above and below, and 16
        between the heading and what follows it — so the top inset drops
        to 16 once the heading has been lifted into the sticky block.

        `min-h-0` is what lets this actually scroll inside the flex
        column; without it the body grows past the card and takes the
        footer off the screen. */}
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto px-4 pb-6",
          stickyTop ? "pt-4" : "pt-6",
        )}
      >
        {children}
      </div>
    </StepContentMotion>

    {/*
      No background of its own, though the frame gives the sticky footer
      `surface/primary`. In light mode that token and the card's
      `container/neutral/white` are both #fdfdfd, so the frame cannot show
      the difference - in dark mode `surface/primary` is pure black
      against the card's #141415, which drew the footer as a black band
      with a strip of card colour below it where the bottom padding
      showed. It is one card; it gets one background.
    */}
    {/* 24 under the footer's button, as the frame measures it (design QA).
        The cards carry no bottom padding of their own, so this is all of it -
        and a step without a footer ends on the body's own 24. */}
    {footer ? <div className="shrink-0 px-4 pb-6">{footer}</div> : null}
  </>
);

/**
 * The docked sheet (`16150:269296`, "Overlay sheet"): the same steps in a 423px
 * card in the bottom-right corner of whatever page the visitor is on.
 *
 * Built to stay out of the way, so it drops everything that makes the modal a
 * modal: no dimmed backdrop, no focus trap, no scroll lock, and it does not
 * take focus when it turns up — the page is the thing being used, the sheet is
 * an offer. Focus only moves into it once the visitor is working through it
 * (@see `onboardingV2EngagedAtom`).
 *
 * The header bar's one control is a "–" that collapses the sheet to its
 * progress pill (`OnboardingDockedPill`) rather than a ✕: the flow stays until
 * the visitor is done with it.
 *
 * `role="dialog"` without `aria-modal`, which is what a non-modal dialog is;
 * the rest of the page stays reachable.
 *
 * 603px tall like the frame, capped so it never runs up under the 80px sticky
 * navbar. Where it sits, and its stacking above the navbar and below the
 * modals, belong to `SheetLayer`, which animates it.
 */
const DockedSheet = ({
  stickyTop,
  children,
  footer,
  ariaLabel,
}: OnboardingModalProps) => {
  const i18n = useI18N();
  const { minimise } = useOnboardingFlow();
  useDockedFootprint("sheet");

  return (
    <div
      id="onboarding-dialog"
      role="dialog"
      aria-label={ariaLabel}
      className="flex h-[603px] max-h-[calc(100dvh-112px)] w-[423px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg border border-border-token-brand-subtle bg-surface-primary shadow-[0px_1px_2px_0px_rgba(7,7,8,0.08),0px_2px_6px_0px_rgba(7,7,8,0.12)] sm:max-h-[calc(100dvh-128px)]"
    >
      {/* Header bar (`1103:53112`): the "–" in the leading slot on
          `surface/tertiary`, nothing else. */}
      <div className="flex shrink-0 items-center gap-2 bg-surface-tertiary px-1 py-2">
        <button
          type="button"
          onClick={minimise}
          aria-label={i18n.get("onboardingMinimiseLabel")}
          aria-expanded="true"
          className="focus-blue flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bg-brand-ghost-default transition-colors hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active"
        >
          <MinusIcon
            size={20}
            aria-hidden="true"
            className="text-fg-neutral-strong"
          />
        </button>
        <div className="flex-1" />
        <div className="h-10 w-10 shrink-0" />
      </div>

      <div className="h-px w-full shrink-0 bg-border-token-brand-minimal" />

      <SheetContent stickyTop={stickyTop} footer={footer}>
        {children}
      </SheetContent>
    </div>
  );
};

const CentredModal = ({
  stickyTop,
  children,
  footer,
  onClose,
  ariaLabel,
}: OnboardingModalProps) => {
  const i18n = useI18N();
  useScrollLock(true);

  /*
   * Each step renders its own copy of this, so moving between steps unmounts
   * and remounts everything here - which is why there is no focus-restore
   * effect. Saving `document.activeElement` on mount and putting focus back on
   * unmount would fire on every step change, not only when the flow closes,
   * throwing focus out of the dialog and back again four times over. Onboarding
   * opens on page load with focus on <body>, so there is nothing to restore;
   * wire one up when a replay entry point gives it something to go back to.
   */

  // The dimmed overlay is `ModalLayer`, which fades it.
  return (
    <FocusTrap
      focusTrapOptions={{
        clickOutsideDeactivates: false,
        escapeDeactivates: () => {
          onClose();
          return false;
        },
        allowOutsideClick: true,
        // The question, not the first tabbable thing the trap happens to
        // find - which is the close button, so opening the flow would
        // announce "Close setup". The heading takes focus itself on each
        // later step change; this is what makes the first one agree.
        initialFocus: `#${ONBOARDING_STEP_HEADING_ID}`,
        fallbackFocus: "#onboarding-dialog",
      }}
    >
      <div
        id="onboarding-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className="flex h-[691px] max-h-full w-[375px] max-w-full flex-col overflow-hidden rounded-lg border border-border-token-brand-subtle bg-container-neutral-white shadow-[0px_2px_6px_0px_rgba(7,7,8,0.12)]"
      >
        {/* Header bar (`1103:53112`): a 40x40 control in the leading slot and
              nothing else — the frame carries no dialog title. */}
        <div className="flex shrink-0 items-center gap-2 px-1 py-2">
          <button
            type="button"
            onClick={onClose}
            aria-label={i18n.get("onboardingCloseLabel")}
            className="focus-blue flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bg-brand-ghost-default transition-colors hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active"
          >
            <XIcon
              size={20}
              aria-hidden="true"
              className="text-fg-neutral-strong"
            />
          </button>
          <div className="flex-1" />
          {/* Trailing slot, empty in the frame — held open so the ✕ stays
                left-aligned rather than centring itself. */}
          <div className="h-10 w-10 shrink-0" />
        </div>

        <div className="h-px w-full shrink-0 bg-border-token-brand-minimal" />

        <SheetContent stickyTop={stickyTop} footer={footer}>
          {children}
        </SheetContent>
      </div>
    </FocusTrap>
  );
};

export default OnboardingModal;
